import { z } from 'zod';
import { activityLevelSchema, biologicalSexSchema, goalSchema, macroPrioritySchema, } from '../tdee/index.js';
/* ==========================================================================
 *  Profile (ISSUE-013, FR-1.1 … FR-1.9)
 *  Slow-changing demographics + dietary context. 1:1 with the user.
 * ========================================================================== */
export const unitsSystemSchema = z.enum(['metric', 'imperial']);
export const energyUnitSchema = z.enum(['kcal', 'kJ']);
/** Full profile as returned by `GET /me/profile`. */
export const profileSchema = z.object({
    userId: z.string(),
    ageYears: z.number().int().min(13).max(120).nullable(),
    biologicalSex: biologicalSexSchema.nullable(),
    heightCm: z.number().positive().max(300).nullable(),
    weightKg: z.number().positive().max(700).nullable(),
    bodyFatPct: z.number().min(1).max(75).nullable(),
    activityLevel: activityLevelSchema.nullable(),
    goal: goalSchema.nullable(),
    macroPriority: macroPrioritySchema.nullable(),
    restrictionsText: z.string().max(2000).nullable(),
    restrictionsTags: z.array(z.string().max(64)).nullable(),
    flaggedEd: z.boolean(),
    unitsSystem: unitsSystemSchema,
    energyUnit: energyUnitSchema,
    locale: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Body for `PATCH /me/profile`. Every field optional (partial update).
 * `flaggedEd` is intentionally NOT user-settable here — the ED safety flag is
 * owned by the screener (ISSUE-018 / ISSUE-058), never by a profile edit.
 */
export const profileUpdateSchema = z
    .object({
    ageYears: z.number().int().min(13).max(120).nullable(),
    biologicalSex: biologicalSexSchema.nullable(),
    heightCm: z.number().positive().max(300).nullable(),
    weightKg: z.number().positive().max(700).nullable(),
    bodyFatPct: z.number().min(1).max(75).nullable(),
    activityLevel: activityLevelSchema.nullable(),
    goal: goalSchema.nullable(),
    macroPriority: macroPrioritySchema.nullable(),
    restrictionsText: z.string().max(2000).nullable(),
    restrictionsTags: z.array(z.string().max(64)).nullable(),
    unitsSystem: unitsSystemSchema,
    energyUnit: energyUnitSchema,
    locale: z.string().min(2).max(10),
})
    .partial()
    .strict();
/* ==========================================================================
 *  Targets (ISSUE-013, FR-5.3)
 *  Versioned + immutable. A correction inserts a new row; rows are never UPDATEd.
 * ========================================================================== */
export const targetSourceSchema = z.enum(['computed', 'user_override', 'ai_suggestion']);
/** A single target version, as returned by the targets endpoints. */
export const targetSchema = z.object({
    id: z.string().uuid(),
    userId: z.string(),
    effectiveFrom: z.string().date(),
    kcal: z.number().int().positive(),
    proteinG: z.number().int().nonnegative(),
    carbsG: z.number().int().nonnegative(),
    fatG: z.number().int().nonnegative(),
    fiberG: z.number().int().nonnegative(),
    macroPriority: macroPrioritySchema.nullable(),
    source: targetSourceSchema,
    cappedBySafety: z.boolean(),
    createdAt: z.string().datetime(),
});
/**
 * Body for `POST /me/targets`. `effectiveFrom` defaults to today server-side and
 * may be a future date, never a past one (history is immutable). `cappedBySafety`
 * is computed by the server (ED safety rules), never trusted from the client.
 */
export const createTargetSchema = z.object({
    effectiveFrom: z.string().date().optional(),
    kcal: z.number().int().positive().max(20000),
    proteinG: z.number().int().nonnegative().max(2000),
    carbsG: z.number().int().nonnegative().max(2000),
    fatG: z.number().int().nonnegative().max(2000),
    fiberG: z.number().int().nonnegative().max(500),
    macroPriority: macroPrioritySchema.nullish(),
    source: targetSourceSchema.optional(),
});
/** Query params for `GET /me/targets?from=&to=` (historical reporting range). */
export const targetsRangeQuerySchema = z.object({
    from: z.string().date().optional(),
    to: z.string().date().optional(),
});
/** Standard error envelope returned by the API (matches the Fastify error handler). */
export const apiErrorSchema = z.object({
    error: z.string(),
    message: z.string(),
    requestId: z.string().optional(),
});
//# sourceMappingURL=profile.js.map